import { useEffect, useRef, useState } from "react";
import { notificationsAPI } from "../api/notificationsApi";
import { socket } from "../socket";
// Poll unread count and broadcast changes across tabs/windows.
export default function useNotificationPoll({ intervalMs = 20000 } = {}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [count, setCount] = useState(0);
  const bcRef = useRef(null);
  const mountedRef = useRef(true);

  // helper to send a cross-tab message (BroadcastChannel preferred, fallback to localStorage)
  const broadcast = (msg) => {
    try {
      if (!bcRef.current && typeof BroadcastChannel !== "undefined") {
        bcRef.current = new BroadcastChannel("notifications");
      }
    } catch (e) {
      bcRef.current = null;
    }

    if (bcRef.current) {
      try {
        bcRef.current.postMessage(msg);
        return;
      } catch (e) {
        // fallthrough to localStorage
      }
    }

    try {
      // localStorage trick: set then remove a timestamped key
      localStorage.setItem("notifications:msg", JSON.stringify({ ...msg, t: Date.now() }));
    } catch (e) {
      // ignore
    }
  };


  // Read userId from localStorage — never hardcode
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userId = String(userData.userId || userData._id || userData.id || '');
  console.log('[useNotificationPoll] resolved userId from localStorage:', userId);


  
  useEffect(() => {
    if (!userId) {
      console.warn('[useNotificationPoll] no userId found — skipping socket connect');
      return;
    }

    // Refresh auth token before connecting (in case module was imported before login)
    const token = localStorage.getItem('authToken');
    if (token) socket.auth = { token };

    if (!socket.connected) {
      console.log('[useNotificationPoll] connecting socket for userId:', userId);
      socket.connect();
    }

    // 🔥 unread count listener
    const handleUnread = ({ count }) => {
      console.log('[useNotificationPoll] socket unread_count received:', count);
      setCount(count);
    };

    socket.on("unread_count", handleUnread);

    // cleanup — do NOT disconnect shared socket here, just remove listeners
    return () => {
      socket.off("unread_count", handleUnread);
    };
  }, [userId]);

  useEffect(() => {
    mountedRef.current = true;

    let intervalId = null;

    const fetchCount = async () => {
      try {
        const c = await notificationsAPI.getUnreadCount();
        const n = Number(c) || 0;
        if (!mountedRef.current) return;
        setUnreadCount((prev) => {
          if (prev !== n) {
            // broadcast change
            broadcast({ kind: "count", count: n });
            return n;
          }
          return prev;
        });
      } catch (err) {
        // silent
        // console.error("useNotificationPoll: failed to fetch count", err);
      }
    };

    // initial
    fetchCount();

    // focus handler
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);

    // interval
    intervalId = setInterval(fetchCount, intervalMs);

    // also listen for incoming requests from other tabs (simple ping)
    const onStorage = (e) => {
      if (e.key === "notifications:msg" && e.newValue) {
        try {
          const msg = JSON.parse(e.newValue);
          if (msg && msg.kind === "request-refresh") fetchCount();
        } catch (e) {
          /* ignore */
        }
      }
    };
    window.addEventListener("storage", onStorage);

    // BroadcastChannel listener as well
    let bc = null;
    try {
      if (typeof BroadcastChannel !== "undefined") {
        bc = new BroadcastChannel("notifications");
        bc.onmessage = (ev) => {
          const msg = ev.data;
          if (!msg) return;
          if (msg.kind === "request-refresh") fetchCount();
        };
        bcRef.current = bc;
      }
    } catch (e) {
      // ignore
    }

    return () => {
      mountedRef.current = false;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      if (intervalId) clearInterval(intervalId);
      try {
        if (bcRef.current) {
          bcRef.current.close();
          bcRef.current = null;
        }
      } catch (e) {}
    };
  }, [intervalMs]);

  // manual refresh function exposed to components
  const refresh = async () => {
    try {
      const c = await notificationsAPI.getUnreadCount();
      const n = Number(c) || 0;
      setUnreadCount((prev) => {
        if (prev !== n) {
          try {
            if (!bcRef.current && typeof BroadcastChannel !== "undefined") bcRef.current = new BroadcastChannel("notifications");
            if (bcRef.current) bcRef.current.postMessage({ kind: "count", count: n });
            else localStorage.setItem("notifications:msg", JSON.stringify({ kind: "count", count: n, t: Date.now() }));
          } catch (e) {}
          return n;
        }
        return prev;
      });
    } catch (e) {
      // ignore
    }
  };

  return { unreadCount, refresh };
}
